import {
    ControllerHelper,
    GroupDownloadDto,
    PostController,
    WikiArticleDownloadDto,
    WikiCategoryDownloadDto,
    WikiController,
    WikiSearchResultDto,
} from 'collaboration-service';
import { ExtendedDataCollection } from 'imaginarity-azure';
import _ from 'lodash';
import * as React from 'react';
import { Actions } from 'services/ApplicationState/Actions';
import { ApplicationState, useAppDispatch, useAppSelector } from 'services/ApplicationState/ApplicationState';
import { shallowCompare, visitWikiCats } from 'services/Helpers';
import { toWikiSearchResult } from './AscsHelpers';
import { AscsArticleMap, AscsProps, AscsValueProps } from './AscsTypes';

const mapper = (state: ApplicationState) => ({
    contentLanguage: state.contentLanguage,
    categories: state.wikiState.categories,
    userGroups: state.userGroups,
});

export const useAscs = (props: AscsProps) => {
    const dispatch = useAppDispatch();
    const { contentLanguage, categories, userGroups } = useAppSelector(mapper, shallowCompare);

    const [articles, setArticles] = React.useState<AscsArticleMap>({});
    const [currentArticle, setCurrentArticle] = React.useState<WikiSearchResultDto | undefined>();
    const [currentCategory, setCurrentCategory] = React.useState<WikiCategoryDownloadDto | undefined>();
    const [currentSubCategory, setCurrentSubCategory] = React.useState<WikiCategoryDownloadDto | undefined>();
    const [show, setShow] = React.useState<string | undefined>();
    const [filter, setFilter] = React.useState<string | undefined>();
    const [searchResults, setSearchResults] = React.useState<WikiSearchResultDto[] | undefined>();
    const [currentSectionId, setCurrentSectionId] = React.useState<string | undefined>();
    const [currentColumnId, setCurrentColumnId] = React.useState<string | undefined>();
    const [searchText, setSearchText] = React.useState<string | undefined>();
    const [selectedGroupid, setSelectedGroupid] = React.useState<string | undefined>();
    const [articleDetailsCache, setArticleDetailsCache] = React.useState<Record<string, WikiSearchResultDto>>({});
    const [loadingArticleIds, setLoadingArticleIds] = React.useState<Set<string>>(new Set());
    const [loadingSubcatIds, setLoadingSubcatIds] = React.useState<Set<string>>(new Set());

    // Refs for values needed in async callbacks to avoid stale closures
    const articlesRef = React.useRef(articles);
    articlesRef.current = articles;
    const selectedGroupidRef = React.useRef(selectedGroupid);
    selectedGroupidRef.current = selectedGroupid;
    const currentCategoryRef = React.useRef(currentCategory);
    currentCategoryRef.current = currentCategory;
    const currentSubCategoryRef = React.useRef(currentSubCategory);
    currentSubCategoryRef.current = currentSubCategory;
    const currentArticleRef = React.useRef(currentArticle);
    currentArticleRef.current = currentArticle;
    const searchResultsRef = React.useRef(searchResults);
    searchResultsRef.current = searchResults;
    const showRef = React.useRef(show);
    showRef.current = show;
    const categoriesRef = React.useRef(categories);
    categoriesRef.current = categories;
    const propsRef = React.useRef(props);
    propsRef.current = props;
    const articleDetailsCacheRef = React.useRef(articleDetailsCache);
    articleDetailsCacheRef.current = articleDetailsCache;

    const toSearchResult = React.useCallback(
        (a: WikiArticleDownloadDto) => toWikiSearchResult(a, contentLanguage),
        [contentLanguage]
    );

    const sectionColumnSelected = React.useCallback((articleId: string, sectionId?: string, columnId?: string) => {
        propsRef.current.sectionColumnSelected?.(articleId, sectionId, columnId);
        setCurrentSectionId(sectionId);
        setCurrentColumnId(columnId);
    }, []);

    const expandSection = React.useCallback((sectionId: string) => {
        setCurrentSectionId(sectionId);
        setCurrentColumnId(undefined);
    }, []);

    const showSubcats = React.useCallback(async (id: string) => {
        let cat = _.find(categoriesRef.current, subcat => subcat.id === id);
        const arts = _.cloneDeep(articlesRef.current);
        if (_.isEqual(cat, currentCategoryRef.current) && currentSubCategoryRef.current === undefined) {
            cat = undefined;
        } else {
            if (!arts[id]) {
                arts[id] = await ControllerHelper.singleCall(
                    { id, groupId: selectedGroupidRef.current ?? propsRef.current.groupid },
                    WikiController.GetArticlesInCatById,
                    true
                );
            }
            if (propsRef.current.onFoundArticles && arts[id]?.[id]) {
                propsRef.current.onFoundArticles(arts[id][id].map(a => toSearchResult(a)));
            }
        }
        setArticles(arts);
        setCurrentCategory(cat);
        setCurrentArticle(undefined);
        setCurrentSubCategory(undefined);
    }, [toSearchResult]);

    // Loads articles for ALL subcategories of a category in one sequential pass (no race conditions)
    const loadSubcatArticles = React.useCallback(async (cat: WikiCategoryDownloadDto) => {
        if (!cat.children?.length) return;
        // Mark all subcats as loading before first await – batched with tree toggle into one render
        setLoadingSubcatIds(prev => {
            const n = new Set(prev);
            for (const subCat of cat.children!) n.add(subCat.id);
            return n;
        });
        const arts = _.cloneDeep(articlesRef.current);
        for (const subCat of cat.children) {
            if (!arts[subCat.id]) {
                arts[subCat.id] = await ControllerHelper.singleCall(
                    { id: subCat.id, groupId: selectedGroupidRef.current ?? propsRef.current.groupid },
                    WikiController.GetArticlesInCatById,
                    true
                );
                // Update articles immediately so count appears as loading indicator disappears
                setArticles({ ...arts });
            }
            setLoadingSubcatIds(prev => { const n = new Set(prev); n.delete(subCat.id); return n; });
        }
    }, []);

    // Loads article list for a subcategory, then sequentially fetches full details (sections/columns)
    const loadSubcatWithSections = React.useCallback(async (subCat: WikiCategoryDownloadDto) => {
        const subcatId = subCat.id;
        setLoadingSubcatIds(prev => { const n = new Set(prev); n.add(subcatId); return n; });
        try {
            const arts = _.cloneDeep(articlesRef.current);
            if (!arts[subcatId]) {
                arts[subcatId] = await ControllerHelper.singleCall(
                    { id: subcatId, groupId: selectedGroupidRef.current ?? propsRef.current.groupid },
                    WikiController.GetArticlesInCatById,
                    true
                );
                setArticles(arts);
                setCurrentSubCategory(subCat);
                setCurrentArticle(undefined);
            }
            setLoadingSubcatIds(prev => { const n = new Set(prev); n.delete(subcatId); return n; });
            const articleList: WikiArticleDownloadDto[] = arts[subcatId]
                ? _.flatMap(Object.values(arts[subcatId]))
                : [];
            for (const article of articleList) {
                const id = article.id ?? '';
                if (articleDetailsCacheRef.current[id]) continue;
                setLoadingArticleIds(prev => { const n = new Set(prev); n.add(id); return n; });
                try {
                    const fetched = await ControllerHelper.singleCall(
                        { id: article.id },
                        PostController.GetSinglePost
                    ) as any as WikiArticleDownloadDto;
                    const result = toSearchResult(fetched);
                    setArticleDetailsCache(prev => ({ ...prev, [id]: result }));
                } finally {
                    setLoadingArticleIds(prev => { const n = new Set(prev); n.delete(id); return n; });
                }
            }
        } catch {
            setLoadingSubcatIds(prev => { const n = new Set(prev); n.delete(subcatId); return n; });
        }
    }, [toSearchResult]);

    const getArticlesInCategory = React.useCallback(async (subCat: WikiCategoryDownloadDto) => {
        const arts = _.cloneDeep(articlesRef.current);
        if (!arts[subCat.id]) {
            arts[subCat.id] = await ControllerHelper.singleCall(
                { id: subCat.id, groupId: selectedGroupidRef.current ?? propsRef.current.groupid },
                WikiController.GetArticlesInCatById,
                true
            );
        }
        setArticles(arts);
        setCurrentSubCategory(subCat);
        setCurrentArticle(undefined);
        if (arts[subCat.id] && propsRef.current.onFoundArticles) {
            propsRef.current.onFoundArticles(arts[subCat.id][subCat.id].map(a => toSearchResult(a)));
        }
    }, [toSearchResult]);

    const showArticle = React.useCallback(async (article: WikiArticleDownloadDto) => {
        const id = article.id ?? '';
        let result: WikiSearchResultDto;
        if (articleDetailsCacheRef.current[id]) {
            result = articleDetailsCacheRef.current[id];
        } else if (currentArticleRef.current?.id !== id) {
            setLoadingArticleIds(prev => { const n = new Set(prev); n.add(id); return n; });
            try {
                const fetched = await ControllerHelper.singleCall(
                    { id: article.id },
                    PostController.GetSinglePost
                ) as any as WikiArticleDownloadDto;
                result = toSearchResult(fetched);
                setArticleDetailsCache(prev => ({ ...prev, [id]: result }));
            } finally {
                setLoadingArticleIds(prev => { const n = new Set(prev); n.delete(id); return n; });
            }
        } else {
            result = currentArticleRef.current!;
            setArticleDetailsCache(prev => ({ ...prev, [id]: result }));
        }
        setCurrentArticle(result!);
        setCurrentSectionId(undefined);
        setCurrentColumnId(undefined);
        if (!propsRef.current.selectSection && !propsRef.current.selectColumns) {
            propsRef.current.sectionColumnSelected?.(id);
        }
    }, [toSearchResult]);

    const selectValue = React.useCallback(async () => {
        const p = propsRef.current;
        if (!p.value) return;
        let v: AscsValueProps = { articleId: '' };
        if (typeof p.value === 'string') {
            if (p.value.startsWith('/')) {
                // Path-based format: /{group}/article/{articleId}[/{sectionId}[/{columnId}]]
                // Category links like /{group}/category/... are not article IDs – skip
                const articleMatch = p.value.match(/^\/[^/]+\/article\/([^/]+)(?:\/([^/]+))?(?:\/([^/]+))?$/);
                if (!articleMatch) return;
                v.articleId = articleMatch[1];
                v.sectionId = articleMatch[2];
                v.columnId = articleMatch[3];
            } else {
                const s = p.value.split('/');
                v.articleId = s[0];
                v.sectionId = s[1];
                v.columnId = s[2];
            }
        } else {
            v = p.value;
        }
        const post = await ControllerHelper.singleCall({ id: v.articleId }, PostController.GetSinglePost);
        const article = post as WikiArticleDownloadDto;

        const findCat = (id: string): WikiCategoryDownloadDto | undefined => {
            let toRet: WikiCategoryDownloadDto | undefined;
            visitWikiCats(categoriesRef.current, c => {
                if (c.id === id) { toRet = c; return false; }
                return true;
            });
            return toRet;
        };

        let subCategory: WikiCategoryDownloadDto | undefined;
        for (const catId of article.categoryIds) {
            subCategory = findCat(catId);
        }

        if (subCategory) {
            let parentCategory: WikiCategoryDownloadDto | undefined = subCategory;
            while (parentCategory?.parentId) {
                parentCategory = findCat(parentCategory.parentId);
            }
            if (parentCategory) await showSubcats(parentCategory.id);
            await getArticlesInCategory(subCategory);
            const searchResult = toSearchResult(article);
            setCurrentArticle(searchResult);
            setCurrentSectionId(v.sectionId);
            setCurrentColumnId(v.columnId);
        }
    }, [showSubcats, getArticlesInCategory, toSearchResult]);

    // Mount – mirrors componentDidMount
    React.useEffect(() => {
        const init = async () => {
            const p = propsRef.current;
            const group = _.find(userGroups, g => g.id === p.groupid);
            if (group?.groupType === 'WIKI') {
                if (!categoriesRef.current) {
                    const cats = await ControllerHelper.singleCall(
                        { groupid: p.groupid },
                        WikiController.GetWikiCategories
                    );
                    dispatch(Actions.setWikiCategories(cats));
                }
                setSelectedGroupid(p.groupid ?? '');
                await selectValue();
            } else {
                setSelectedGroupid(p.groupid ?? '');
            }
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Value change – mirrors componentDidUpdate check on value prop
    const prevValueRef = React.useRef(props.value);
    React.useEffect(() => {
        if (!_.isEqual(prevValueRef.current, props.value)) {
            prevValueRef.current = props.value;
            selectValue();
        }
    }, [props.value, selectValue]);

    const onChangeGroup = React.useCallback(async (data?: GroupDownloadDto) => {
        const cats = await ControllerHelper.singleCall(
            { groupid: data!.id },
            WikiController.GetWikiCategories
        );
        dispatch(Actions.setWikiCategories(cats));
        setSelectedGroupid(data!.id);
        propsRef.current.onGroupChange?.(data!);
    }, [dispatch]);



    const onSearched = React.useCallback((results?: ExtendedDataCollection<WikiSearchResultDto, string>) => {
        setSearchResults(results?.elements);
        setSearchText(results?.data);
        propsRef.current.onFoundArticles?.(results?.elements);
    }, []);

    const onShow = React.useCallback((s: string) => {
        setShow(showRef.current !== undefined ? undefined : s);
        setCurrentCategory(undefined);
        setCurrentSubCategory(undefined);
        setCurrentArticle(undefined);
        if (propsRef.current.onFoundArticles) {
            if (s === 'filter') propsRef.current.onFoundArticles(undefined);
            else propsRef.current.onFoundArticles(searchResultsRef.current);
        }
    }, []);

    const collapseSubCategory = React.useCallback(() => {
        setCurrentSubCategory(undefined);
        setCurrentArticle(undefined);
        setCurrentSectionId(undefined);
        setCurrentColumnId(undefined);
    }, []);

    const collapseArticle = React.useCallback(() => {
        setCurrentArticle(undefined);
        setCurrentSectionId(undefined);
        setCurrentColumnId(undefined);
    }, []);

    return {
        articles, currentArticle, currentCategory, currentSubCategory,
        show, filter, searchText, selectedGroupid,
        currentSectionId, currentColumnId,
        contentLanguage, categories, userGroups,
        showSubcats, loadSubcatArticles, loadSubcatWithSections, getArticlesInCategory, showArticle,
        sectionColumnSelected, expandSection,
        collapseSubCategory, collapseArticle,
        onChangeGroup, onSearched, onShow,
        setFilter,
        articleDetailsCache, loadingArticleIds, loadingSubcatIds,
    };
};
